import { useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import RefreshContext from "../RefreshContext";
import TransactionsTable  from "./TransactionsTable";
import Dollars from "./Dollars";
import apiClient from "../lib/apiClient";

export default function EditPayee({ payee, onHide }) {
  const [regex, setRegex] = useState(payee.regex);
  const [category, setCategory] = useState(payee.category);
  const [name, setName] = useState(payee.name);
  const [categories, setCategories] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [matchData, setMatchData] = useState({
    percent_match: 0,
    transactions: [],
    match_count: 0
  });

  const { refresh, refreshKey } = useContext(RefreshContext);

  useEffect(() => {
    const fetchCategories = async () => {
      const {data} = await apiClient.get("/api/categories/all");
      setCategories(data);
    };

    fetchCategories();
  }, [payee, refreshKey]);

  useEffect(() => {
    setName(payee.name);
    setCategory(payee.category);
    setRegex(payee.regex);
  }, [payee]);

  useEffect(() => {
    // TODO debounce this call.
    async function fetchTransactions() {
      const path = `/api/transactions/regex_match?regex=${regex}`;
      const {data} = await apiClient.get(path);
      setMatchData(data);
    };

    fetchTransactions();
  }, [regex, payee, refreshKey]);


  function handleSave() {
    const data = { name, category, regex };

    apiClient.put(`/api/payees/${payee.id}`, data)
      .then(() => {
        onHide();
        refresh();
      }).catch(console.error);
  }

  function handleDelete() {
    const proceed = window.confirm(`Delete ${payee.name}?\n\n` +
      "No transactions will be deleted, but we will try to recategorize them.");
    if (!proceed) return;

    setIsDeleting(true);
    apiClient.delete(`/api/payees/${payee.id}`)
      .then(() => {
        setIsDeleting(false);
        onHide();
        refresh();
      }).catch(console.error);
  }

  const { percent_match, dollar_match, transactions, match_count } = matchData

  let statusStyle = "text-muted";
  if (percent_match > 10) {
    statusStyle = "text-danger"
  } else if (percent_match > 3) {
    statusStyle = "text-warning"
  } else if (match_count === 0) {
    statusStyle = "text-danger"
  }

  if (isDeleting) {
    return <Modal size={"sm"} show={isDeleting} centered>
      <Modal.Body>
        <div className={"text-center"}>
          <p>Deleting {payee.name}</p>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Deleting {name} and recategorizing...</span>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  }

  return <Modal size={"xl"} scrollable centered show onHide={onHide}>
    <datalist id="categoryList">
      {categories.map((category, i) => <option key={i} value={category.name} />)}
    </datalist>

    <Modal.Header>
      <h4>Edit Payee: {name}</h4>
    </Modal.Header>

    <Modal.Body style={{ height: '50vw', minHeight: "450px" }}>
      <div className={"d-flex mb-2"}>
        <div className="ms-2">
          <label htmlFor={"name"} className="form-label">Name</label>
          <input type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-control" id="name" />
        </div>
        <div className={"ms-2"}>
          <label htmlFor="category" className="form-label">Category</label>
          <input type="text"
            value={category}
            list={"categoryList"}
            onChange={(e) => setCategory(e.target.value)}
            className="form-control" id="category" />
        </div>
        <div className={"ms-2 flex-grow-1"}>
          <label htmlFor="regex" className="form-label">Match Expression</label>
          <input type="text"
            value={regex}
            className="form-control"
            id="regex"
            onChange={(e) => setRegex(e.target.value)}
          />
        </div>
      </div>

      <div className={"row"}>
        <div className={"col-12 pt-2"}>
          <h5>Matching Transactions</h5>
          {transactions.length > 0 && (
            <TransactionsTable transactions={transactions} columns={["account_name"]} />
          )}
          {match_count === 0 && <p>
            No transactions match <code>{regex}</code> for {name}. Try a different match pattern.
          </p>}
        </div>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <div className={"flex-grow-1"}>
      </div>
      {match_count > 0 && <p className={statusStyle}>
        {match_count} matches, or {percent_match}% of all transactions, (<Dollars amount={dollar_match} />)
      </p>}
      {match_count === 0 && <p className={"text-danger"}>
        No matches found.
      </p>}
      <button type="button" className="btn btn-outline-secondary"
        onClick={onHide}>
        Cancel
      </button>
      <button type="button" className="btn btn-outline-danger"
        onClick={handleDelete}>
        Delete Payee
      </button>
      <button disabled={percent_match > 10 || match_count === 0}
        type="button" className="btn btn-primary"
        onClick={handleSave}>
        Save
      </button>
    </Modal.Footer>
  </Modal>
}

function HelpInfo() {
  return <div className={"card"}>
    <div className={"card-body"}>
      <h5>Help</h5>
      <p>
        Following are common reasons why a payee might be miscategorized:
      </p>
      <ul>
        <li>
          A payee is really an internal transfer that we haven't recognized (for example paying off a credit
          card). In this case, you should mark the payee as an internal transfer and its
          matching transactions will be skipped when performing spend analysis.
        </li>
        <li>
          A payee's match expression isn's specific enough and is matching too many transactions.
          In this case, you should edit the payee's match expression to be more specific.
        </li>
        <li>
          The payee shouldn't exist at all. Sometimes we accidentally create a payee like
          "Transfer" or "Payment" that shouldn't exist. In this case, you should delete the payee.
          We will attempt to re-categorize the transactions that were assigned to this payee.
        </li>
        <li>
          The Payee belongs to an incorrect category. We might for example categorize
          "Amazon.com" as "Groceries" when it should be "Shopping". In this case, you should
          change the category of the payee.
        </li>
      </ul>
    </div>
  </div>
}